import sampleImage from "./../sampleImage";
import React, { useEffect, useState } from "react";
import DesktopInstructions from "./DesktopInstructions";

/**
 * Loader component
 *
 * @returns {JSX.Element}
 */
const Loader = () => {
    return (
        <svg
            className="animate-spin h-12 w-12 text-primary-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            ></circle>
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
        </svg>
    );
};

/**
 * SkincareComponent is a functional React component using the YMK module to
 * perform skincare analysis and provide results, including a snapshot and
 * snapshot with a score. It calls the onScanFinish prop function with the
 * results of these processes
 *
 * @param {Object} props
 * @param {function} props.onScanFinish - The callback function to be called
 * with the YMK analysis results
 */
const SkincareComponent = ({ onScanFinish, setIsOpen }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [opened, setOpened] = useState(false);

    useEffect(() => {
        const cid = import.meta.env.VITE_CID;
        const apiKey = import.meta.env.VITE_API_KEY;
        const sourceDomain = import.meta.env.VITE_SOURCE_DOMAIN;

        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = `${sourceDomain}${cid}/sdk.js?apiKey=${apiKey}`;
        document.getElementsByTagName("head")[0].appendChild(script);

        script.onload = () => {
            ymkAsyncInit();
        };

        return () => {
            script.remove();
        };
    }, []);

    /**
     * Bypasses the YMK widget and calls the onScanFinish prop function with
     * This is only for testing purposes with Dusk
     */
    const bypassWidgetResults = () => {
        onScanFinish({
            results: {
                timed: 1263,
                redness: 75,
                skinAge: 24,
                texture: 77,
                ageSpots: 81,
                moisture: 77,
                oiliness: 73,
                wrinkles: 80,
                skinHealth: 76,
                darkCirclesV2: 70,
            },
            snapshot: sampleImage,
            snapshotWithScore: sampleImage,
        });
    };

    /**
     * Handles the event triggered when the scan finishes by storing the
     * @param {*} report
     */
    async function handleSkinAnalysisUpdated(report) {
        try {
            setIsLoading(true);

            const snapshot = await handleSnapshot();
            const concerns = [
                "ageSpots",
                "wrinkles",
                "texture",
                "darkCirclesV2",
            ];
            const snapshotWithScore = await handleSnapshotWithScore(concerns);

            onScanFinish({
                results: report,
                snapshot,
                snapshotWithScore,
            });
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    }

    /**
     * Initializes the YMK module, sets up event listeners, and opens the
     * skincare analyzer
     */
    const ymkAsyncInit = () => {
        YMK.addEventListener("skinAnalysisUpdated", handleSkinAnalysisUpdated);
        document.addEventListener("__skinAnalysisUpdated", bypassWidgetResults);

        YMK.addEventListener("consentUIShowed", () => {
            setIsLoading(false);
        });

        YMK.addEventListener("opened", () => {
            setOpened(true);
        });

        YMK.addEventListener("closed", () => {
            setOpened(false);
        });

        YMK.openSkincare();
    };

    /**
     * Opens the YMK skincare analyzer
     */
    const openSkincare = () => {
        YMK.openSkincare();
    };

    /**
     * Takes a snapshot of the YMK skincare analysis using the base64 format
     *
     * @returns {Promise<string>} A promise resolving to the base64-encoded
     * snapshot image
     */
    const handleSnapshot = () => {
        return new Promise((resolve) => {
            YMK.snapshot("base64", function (image) {
                resolve(image);
            });
        });
    };

    /**
     * Takes a snapshot of the YMK skincare analysis with a score for the
     * specified skincare concerns
     *
     * @param {Array<string>} skincareconcerns - Array of skincare concerns
     * to include in the snapshot
     * @returns {Promise<string>} A promise resolving to the base64-encoded
     * snapshot image with a score
     */
    const handleSnapshotWithScore = (skincareconcerns) => {
        return YMK.snapshotSkincareResult(skincareconcerns, {
            score: true,
            legend: true,
            skinAge: true,
        });
    };

    return (
        <div className="flex w-full justify-center relative">
            <div className="w-full md:flex md:container md:mx-auto md:justify-center md:space-x-16">
                <div
                    className="hidden md:block bg-[#f5f5f5] py-12 flex-1 max-w-xl w-full"
                    dusk="desktop-instructions"
                >
                    <DesktopInstructions />
                </div>
                <div className="w-full min-h-[600px] md:w-[600px] relative">
                    {isLoading && (
                        <div
                            className="absolute inset-0 flex justify-center items-center z-20"
                            dusk="widget-loading"
                        >
                            <Loader />
                        </div>
                    )}

                    <div className="flex w-full md:hidden justify-center mb-6">
                        <button
                            onClick={() => setIsOpen(true)}
                            dusk="open-instructions-button"
                            className="inline-flex outline-none justify-center items-center border border-primary-400 px-6 py-2 text-sm w-44"
                        >
                            RECOMENDACIONES
                            <img
                                src="/img/info-icon.svg"
                                className="w-4 h-4 ml-2"
                                alt=""
                            />
                        </button>
                    </div>

                    <div className="relative flex justify-center items-center">
                        {!opened && !isLoading && (
                            <div className="absolute inset-0 w-full min-h-[600px] flex flex-col justify-center items-center z-10">
                                <img
                                    src="/img/home-icon.png"
                                    className="h-60 h-60 mb-6 block object-contain"
                                    alt=""
                                />
                                <button
                                    onClick={openSkincare}
                                    className="inline-flex outline-none justify-center border border-transparent bg-primary-400 px-4 py-2 text-sm font-medium text-white w-44"
                                >
                                    EMPEZAR EVALUACIÓN
                                </button>
                            </div>
                        )}
                        <div
                            id="YMK-module"
                            className="rounded-md overflow-hidden relative z-20 flex items-center justify-center"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SkincareComponent;
